import { useState, useRef, useEffect, useMemo } from 'react';

import { Box, Flex, Grid, GridItem } from '@chakra-ui/react';
import { Checkbox, Avatar, AvatarImage, AvatarFallback } from '@mesh.ai/rabbit';
import useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges from 'components/hooks/useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useToast } from 'components/hooks/useToast';
import { employeeMiniProfile } from 'interfaces/EmployeeMiniProfile';
import { z } from 'zod';

import { MeshMavenWithYellowBackground } from 'assets/assetsV1/iconComponents/MeshMavenWithYellowBackground';

import { Divider } from 'uiToolkitV1/Divider';
import { InputV2 } from 'uiToolkitV1/InputV2';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { ACTION_BUTTON_OPTIONS, REVIEW_ACTIONS, REVIEW_ACTION_CATEGORIES_OPTIONS, API_KEYS_TAGS } from '../constant';
import { editActionPlanItem } from '../hooks/dataHooks';
import type { EditedActionPlanItem } from '../types';
import { ActionAgendaInputWithCategory } from './ActionAgendaInputWithCategory';
import { ActionButtonDropdown } from './ActionButtonDropdown';

const getAccessibleModuleOption = (
  module: keyof typeof REVIEW_ACTION_CATEGORIES_OPTIONS,
  actionButtonOptions: Partial<typeof ACTION_BUTTON_OPTIONS>
) => {
  const moduleOption = actionButtonOptions[module];

  // Return the selected module option if it has access
  if (moduleOption?.hasAccess()) return moduleOption;

  // Find and return the first alternative option with access
  return Object.values(actionButtonOptions).find((option) => option.hasAccess() && option !== moduleOption);
};

type employeeMiniProfileType = z.infer<typeof employeeMiniProfile>;

type ActionListItemProps = {
  id: number;
  title: string;
  isChecked: boolean;
  tag: keyof typeof REVIEW_ACTION_CATEGORIES_OPTIONS;
  module: keyof typeof REVIEW_ACTION_CATEGORIES_OPTIONS;
  onActionSelect: (actionType: keyof typeof REVIEW_ACTIONS, actionItemId: number) => void;
  fromEmployee: employeeMiniProfileType;
  reviewCycle: string;
  refetchActionPlanList: () => void;
  isMlGenerated: boolean;
  isEdited: boolean;
};

type EditAgenda = {
  isCheckboxTrigger?: boolean;
  checkValue?: boolean;
  selectedTag?: string;
  isTagTrigger?: boolean;
  isInputTrigger?: boolean;
};

export const ActionListItem = ({
  id,
  title,
  isChecked,
  tag,
  module,
  onActionSelect,
  fromEmployee,
  reviewCycle,
  refetchActionPlanList,
  isMlGenerated,
  isEdited,
}: ActionListItemProps) => {
  // in future, move the goal permission check to the hasAccess function in constant for better readability
  // currently we are checking via custom hook so unable to move that to constants
  const { canCreateKeyResultUnderKeyResult, canCreateKeyResultUnderObjective, canCreateKeyResultAtRootLevel } =
    useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges();
  const canCreateGoals: boolean =
    canCreateKeyResultAtRootLevel || canCreateKeyResultUnderObjective || canCreateKeyResultUnderKeyResult;

  // filter out the goals action button if we don't have permission to create a goal
  const FILTERED_ACTION_BUTTON_OPTIONS = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(ACTION_BUTTON_OPTIONS).filter(([key]) => canCreateGoals || key !== REVIEW_ACTIONS.GOAL)
      ),
    [canCreateGoals]
  );

  const moduleOption = getAccessibleModuleOption(module, FILTERED_ACTION_BUTTON_OPTIONS);
  const { displayName: fromEmployeeName, profilePhoto: fromEmployeeProfilePhoto = '' } = fromEmployee;
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const dropdownContainerRef = useRef(null);
  const inputBoxRef = useRef(null);
  const [isEditState, setIsEditState] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const toast = useToast();
  const { moduleName: goalsModuleCustomName } = useOrganisationSettings();
  const isGoalsModule = module === API_KEYS_TAGS.GOAL;

  const handleInputChange = (e) => {
    setInputValue(e.currentTarget.value);
  };

  useEffect(() => {
    if (isEditState) {
      setTimeout(() => {
        inputBoxRef.current.focus();
        setInputValue(title);
      }, 100);
    }
  }, [isEditState, title]);

  const editAgenda = async ({
    isCheckboxTrigger,
    checkValue,
    isTagTrigger,
    selectedTag,
    isInputTrigger,
  }: EditAgenda) => {
    try {
      if (isInputTrigger && inputValue === title) return;
      if (isTagTrigger && selectedTag === tag) return;

      const editedActionPlanItem: EditedActionPlanItem = {
        actionItemText: isInputTrigger ? inputValue : title,
        tag: isTagTrigger ? selectedTag : tag,
        isCompleted: isCheckboxTrigger ? checkValue : isChecked,
        reviewActionPlanId: id,
      };

      await editActionPlanItem({ editedActionPlanItem, reviewCycle });
      refetchActionPlanList();
      setInputValue('');
      setIsEditState(false);
      if (!isCheckboxTrigger) {
        toast({
          title: 'Action item edited successfully',
          status: 'success',
        });
      }
    } catch (error) {
      console.error('Error editing action plan item : ', error);

      toast({
        title: error?.message || 'An unknown error has occurred, please try again',
        status: 'error',
      });
    }
  };

  const handleCheckbox = (checkValue) => {
    editAgenda({ isCheckboxTrigger: true, checkValue });
  };

  const handleCategoryChange = (selectedCategory) => {
    editAgenda({ selectedTag: selectedCategory, isTagTrigger: true });
  };

  const checkifEnterIsPressed = (e) => {
    if (e.key === 'Enter') {
      editAgenda({ isInputTrigger: true });
    }
  };

  const handleTextBodyClick = () => {
    setIsEditState(true);
    setIsHovered(false);
  };

  const handleInputBlur = () => {
    editAgenda({ isInputTrigger: true });
    setIsEditState(false);
  };

  const handleMouseOver = (hoverAction) => {
    setIsHovered(hoverAction);
  };

  return (
    <>
      <Grid templateColumns="20px minmax(70%, 1fr) minmax(200px, 250px)" p="12px" gap="12px">
        <GridItem display="flex">
          <Flex h="100%" alignItems="flex-start">
            <Checkbox checked={isChecked} onCheckedChange={(e) => handleCheckbox(e)} />
          </Flex>
        </GridItem>
        <GridItem>
          <Flex w="100%" flexDirection="column">
            <Flex grow={2}>
              <Box ref={inputRef} width="100%">
                {isEditState ? (
                  <InputV2
                    paddingRight="12px"
                    ref={inputBoxRef}
                    type="text"
                    isDashed
                    value={inputValue}
                    onChange={(e) => handleInputChange(e)}
                    style={{
                      textDecoration: isChecked ? 'line-through' : 'none',
                      marginTop: '-12px',
                    }}
                    onKeyDown={checkifEnterIsPressed}
                    onBlur={handleInputBlur}
                  />
                ) : (
                  <TextBodyMedium
                    paddingRight="12px"
                    paddingBottom={title ? '8px' : '27px'}
                    bold
                    borderBottom={isHovered ? '1px solid #4C4949' : '1px dashed #999999'}
                    style={{ textDecoration: isChecked ? 'line-through' : 'none' }}
                    onClick={handleTextBodyClick}
                    onMouseOver={() => handleMouseOver(true)}
                    onMouseLeave={() => handleMouseOver(false)}
                  >
                    {title || ''}
                  </TextBodyMedium>
                )}
              </Box>
            </Flex>
            <Flex alignItems="center" mt="4px">
              <Flex alignItems="center" gap="4px">
                <Flex justifyContent="center">
                  <Box>
                    <TextLabel color="text.g2" width="max-content">
                      {isMlGenerated ? 'Recommended by' : 'Added by'}
                    </TextLabel>
                  </Box>
                </Flex>
                <Flex justifyContent="center">
                  <Box>
                    <TextLabel color="text.g2" width="max-content">
                      {isMlGenerated ? (
                        <MeshMavenWithYellowBackground boxSize="14px" />
                      ) : (
                        <Avatar size="xs" style={{ marginTop: '2px' }}>
                          <AvatarImage src={fromEmployeeProfilePhoto} alt={`profile picture of ${fromEmployeeName}`} />
                          <AvatarFallback>{fromEmployeeName.slice(0, 2)}</AvatarFallback>
                        </Avatar>
                      )}
                    </TextLabel>
                  </Box>
                </Flex>
                <Flex justifyContent="center" width="max-content">
                  <Box>
                    <TextLabel color="text.g2">{isMlGenerated ? 'Maven' : fromEmployeeName}</TextLabel>
                  </Box>
                </Flex>
                <Flex justifyContent="center">
                  <TextLabel color="text.g2"> • </TextLabel>
                </Flex>
                <Flex>
                  <Box>
                    <ActionAgendaInputWithCategory
                      selectedCategory={tag}
                      onCategoryChange={handleCategoryChange}
                      dropdownRef={dropdownRef}
                      containerRef={dropdownContainerRef}
                      hideAgendaInput
                    />
                  </Box>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </GridItem>
        <GridItem>
          <Flex
            alignItems="center"
            justifyContent="stretch"
            width="100%"
            sx={{
              '> div.rabbit-ds': {
                width: '100%',
              },
            }}
          >
            <ActionButtonDropdown
              id={id}
              moduleLabel={moduleOption.label(isGoalsModule ? goalsModuleCustomName : null)}
              moduleIcon={moduleOption.Icon}
              onActionSelect={onActionSelect}
              isAddedByEmployee={!isMlGenerated} // explain-why bottom sheet check
              disabled={isEditState}
              canCreateGoals={canCreateGoals}
              isGoalsModule={isGoalsModule}
              goalsModuleCustomName={goalsModuleCustomName}
              isEdited={isEdited}
            />
          </Flex>
        </GridItem>
      </Grid>
      <Divider />
    </>
  );
};
